import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Permission, roleHasPermissions } from '@lizrd/contracts';
import { getBillingSummary } from '../lib/api';
import { BETA_MODE } from '../lib/beta';
import { useAuthStore } from '../store/auth';
import { Alert } from './ui/alert';

/**
 * App-wide billing banners, scoped by what each role can actually fix:
 *
 * 1. **Billing-access lost** — a connected cloud account's billed-cost read broke.
 *    Shown to anyone who can manage integrations (they can reconnect it), names the
 *    specific account(s), and links to the right domain's Integrations page. A
 *    warning during the 5-day grace, danger once it lapses.
 * 2. **Payment method** — payments are active but no card is on file and the trial
 *    has ended, so charges will fail. Admins only (only they can add a card), links
 *    to the Billing page.
 *
 * Both are billing-scoped — the optimization experience is never blocked.
 */
export function BillingAccessBanner() {
  const role = useAuthStore((s) => s.user?.role);
  const canIntegrations = role ? roleHasPermissions(role, [Permission.IntegrationsManage]) : false;
  const canManageOrg = role ? roleHasPermissions(role, [Permission.OrgManage]) : false;
  const summary = useQuery({
    queryKey: ['billing-summary'],
    queryFn: getBillingSummary,
    enabled: canIntegrations || canManageOrg,
  });
  const s = summary.data;
  if (!s) return null;

  const access = s.billingAccess;
  const showAccess = canIntegrations && !access.ok;
  // Payments active but no card + trial over → charges will fail. Gated on !BETA_MODE
  // — the same flag that removes the /settings/billing route this links to — because
  // API payments (STRIPE_ENABLED) and the web Billing route (VITE_BETA_MODE) are
  // independent flags that diverge (e.g. staging/local: Stripe on, route hidden), so
  // status can be 'none' while the page doesn't exist. Tying the banner to BETA_MODE
  // guarantees the link only appears when its destination does.
  const showPayment =
    !BETA_MODE && canManageOrg && s.paymentMethod.status === 'none' && !s.trial.active;
  if (!showAccess && !showPayment) return null;

  const affected = access.affected ?? [];
  const label = (a: (typeof affected)[number]) =>
    `${a.provider} · ${a.displayName} (${a.domainName})`;
  const accountList =
    affected.length === 0
      ? 'a cloud account'
      : affected.slice(0, 2).map(label).join(', ') +
        (affected.length > 2 ? `, and ${affected.length - 2} more` : '');
  const fixLink = affected[0] ? `/domains/${affected[0].domainId}/integrations` : '/domains';

  return (
    <>
      {showAccess && (
        <Alert variant={access.restricted ? 'danger' : 'warning'} className="mb-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>
              {access.restricted
                ? `Billing is paused — we can no longer read billed cost for ${accountList}. Restore access to resume; your optimization insights are unaffected.`
                : `We've lost billing access to ${accountList} — reconnect within ${access.daysRemaining} day${
                    access.daysRemaining === 1 ? '' : 's'
                  } to keep your billing accurate.`}
            </span>
            <Link to={fixLink} className="shrink-0 font-semibold text-bone underline">
              Fix billing access
            </Link>
          </div>
        </Alert>
      )}
      {showPayment && (
        <Alert variant="warning" className="mb-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>
              No payment method on file — add a card so your subscription isn&rsquo;t interrupted
              now that your trial has ended.
            </span>
            <Link to="/settings/billing" className="shrink-0 font-semibold text-bone underline">
              Add payment method
            </Link>
          </div>
        </Alert>
      )}
    </>
  );
}
